import { Injectable } from '@angular/core';
import { GoogleMapsService } from './google-maps.service';
import { WindowService } from './window.service';
import { environment } from '../../enviroments/environment';
import { TelemetryService } from './telemetry.service';

interface CachedGeocode {
  result: google.maps.GeocoderResult;
  cachedAt: number;
}

interface SerializedCachedGeocode {
  cachedAt: number;
  result: {
    formatted_address?: string;
    address_components?: google.maps.GeocoderAddressComponent[];
    geometry?: {
      location?: {
        lat: number;
        lng: number;
      };
    };
  };
}

@Injectable({
  providedIn: 'root'
})
export class AddressResolutionService {
  private readonly ttlMs = environment.geocodeCache?.ttlMs ?? (72 * 60 * 60 * 1000);
  private readonly maxEntries = environment.geocodeCache?.maxEntries ?? 10;
  private readonly maxRetentionMs = (environment.geocodeCache?.maxRetentionDays ?? 5) * 24 * 60 * 60 * 1000;
  private readonly storageMode: 'session' | 'local' = environment.geocodeCache?.storageMode === 'session' ? 'session' : 'local';
  private readonly cache = new Map<string, CachedGeocode>();
  private readonly inFlight = new Map<string, Promise<google.maps.GeocoderResult | undefined>>();
  private readonly trackedCacheHitTelemetry = new Set<string>();
  private readonly storagePrefix = 'addr-resolve:';
  private readonly metrics = {
    resolves: 0,
    apiCalls: 0,
    apiOk: 0,
    apiNoResult: 0,
    cacheHits: 0,
    cacheMisses: 0,
    storageHits: 0,
    storageMisses: 0,
    storageSaves: 0,
  };

  constructor(
    private googleMapsService: GoogleMapsService,
    private windowService: WindowService,
    private telemetryService: TelemetryService
  ) {}

  async resolve(
    address: string,
    zip: string = '',
    source: string = 'unknown',
    debugLog?: (msg: string, ...args: any[]) => void
  ): Promise<google.maps.GeocoderResult | undefined> {
    const resolveTimer = Date.now();
    this.metrics.resolves += 1;
    const win = this.windowService.window;
    if (!win) {
      debugLog?.('[MAPS-DEBUG] [ADDR] SKIP_SSR', { source, address, zip });
      debugLog?.('[MAPS-DEBUG] [ADDR] SKIP_SSR_STORAGE', { source });
      return undefined;
    }

    const normalizedAddress = this.normalizeAddress(address || '');
    const normalizedZip = this.normalizeZip(zip || '');

    if (!normalizedAddress) {
      debugLog?.('[MAPS-DEBUG] [ADDR] SKIP_EMPTY_ADDRESS', { source, address, zip });
      return undefined;
    }

    const key = `${normalizedAddress}|${normalizedZip}`;
    debugLog?.('[MAPS-DEBUG] [ADDR] CANONICAL_KEY', {
      source,
      key,
      address,
      zip,
    });
    const now = Date.now();
    this.cleanupInMemory(now);
    this.cleanupStorage(win, now, source, debugLog);

    const cached = this.cache.get(key);
    if (cached) {
      const ageMs = now - cached.cachedAt;
      if (ageMs < this.ttlMs) {
        this.metrics.cacheHits += 1;
        debugLog?.('[MAPS-DEBUG] [ADDR] CACHE_HIT', { source, key, ageMs });
        this.logMetricsSummary(debugLog, source, key);
        this.trackCacheHitOnce('cache_memory', key, Date.now() - resolveTimer, source, debugLog);
        return cached.result;
      }
      this.cache.delete(key);
      debugLog?.('[MAPS-DEBUG] [ADDR] CACHE_EXPIRED', { source, key, ageMs });
    } else {
      this.metrics.cacheMisses += 1;
      debugLog?.('[MAPS-DEBUG] [ADDR] CACHE_MISS', { source, key });
    }

    const sessionCached = this.readFromStorage(win, key, now, source, debugLog);
    if (sessionCached) {
      this.cache.set(key, sessionCached);
      debugLog?.('[MAPS-DEBUG] [ADDR] CACHE_SAVE', { source, key, from: 'session' });
      this.logMetricsSummary(debugLog, source, key);
      this.trackCacheHitOnce('cache_storage', key, Date.now() - resolveTimer, source, debugLog);
      return sessionCached.result;
    }

    const inFlight = this.inFlight.get(key);
    if (inFlight) {
      debugLog?.('[MAPS-DEBUG] [ADDR] INFLIGHT_HIT', { source, key });
      this.trackCacheHitOnce('inflight_shared', key, Date.now() - resolveTimer, source, debugLog);
      return inFlight;
    }

    this.metrics.apiCalls += 1;
    const request = this.googleMapsService.geocodeAddress(address, zip, debugLog, source);
    this.inFlight.set(key, request);
    debugLog?.('[MAPS-DEBUG] [ADDR] INFLIGHT_CREATE', { source, key });

    let result: google.maps.GeocoderResult | undefined;
    try {
      result = await request;
    } finally {
      this.inFlight.delete(key);
      debugLog?.('[MAPS-DEBUG] [ADDR] INFLIGHT_CLEAR', { source, key });
    }

    if (result) {
      this.metrics.apiOk += 1;
      const entry: CachedGeocode = { result, cachedAt: Date.now() };
      this.cache.set(key, entry);
      debugLog?.('[MAPS-DEBUG] [ADDR] CACHE_SAVE', { source, key });
      this.writeToStorage(win, key, entry, source, debugLog);
    } else {
      this.metrics.apiNoResult += 1;
    }

    this.logMetricsSummary(debugLog, source, key);

    return result;
  }

  private readFromStorage(
    win: Window,
    key: string,
    now: number,
    source: string,
    debugLog?: (msg: string, ...args: any[]) => void
  ): CachedGeocode | undefined {
    try {
      const raw = this.getStorage(win).getItem(this.storagePrefix + key);
      if (!raw) {
        this.metrics.storageMisses += 1;
        debugLog?.('[MAPS-DEBUG] [ADDR] STORAGE_MISS', { source, key, storageMode: this.storageMode });
        return undefined;
      }

      const parsed = JSON.parse(raw) as SerializedCachedGeocode;
      const ageMs = now - (parsed.cachedAt || 0);
      if (!parsed.cachedAt || ageMs >= this.ttlMs || ageMs >= this.maxRetentionMs) {
        this.getStorage(win).removeItem(this.storagePrefix + key);
        debugLog?.('[MAPS-DEBUG] [ADDR] STORAGE_EXPIRED', { source, key, ageMs, storageMode: this.storageMode });
        this.metrics.storageMisses += 1;
        return undefined;
      }

      const lat = parsed.result?.geometry?.location?.lat;
      const lng = parsed.result?.geometry?.location?.lng;
      if (typeof lat !== 'number' || typeof lng !== 'number') {
        this.getStorage(win).removeItem(this.storagePrefix + key);
        debugLog?.('[MAPS-DEBUG] [ADDR] STORAGE_MISS', { source, key, reason: 'invalid-payload', storageMode: this.storageMode });
        this.metrics.storageMisses += 1;
        return undefined;
      }

      const reconstructed: google.maps.GeocoderResult = {
        formatted_address: parsed.result.formatted_address ?? '',
        address_components: parsed.result.address_components ?? [],
        geometry: {
          location: {
            lat: () => lat,
            lng: () => lng,
          } as unknown as google.maps.LatLng,
        } as google.maps.GeocoderGeometry,
      } as google.maps.GeocoderResult;

      debugLog?.('[MAPS-DEBUG] [ADDR] STORAGE_HIT', { source, key, ageMs, storageMode: this.storageMode });
      this.metrics.storageHits += 1;
      return {
        result: reconstructed,
        cachedAt: parsed.cachedAt,
      };
    } catch (err) {
      this.metrics.storageMisses += 1;
      debugLog?.('[MAPS-DEBUG] [ADDR] STORAGE_MISS', { source, key, reason: 'exception', err, storageMode: this.storageMode });
      return undefined;
    }
  }

  private trackCacheHitOnce(
    resolutionSource: 'cache_memory' | 'cache_storage' | 'inflight_shared',
    key: string,
    durationMs: number,
    source: string,
    debugLog?: (msg: string, ...args: any[]) => void
  ): void {
    const telemetryKey = `${resolutionSource}|${key}`;
    if (this.trackedCacheHitTelemetry.has(telemetryKey)) {
      debugLog?.('[MAPS-DEBUG] [ADDR] SKIP_DUPLICATE_CACHE_HIT_TELEMETRY', { source, key, resolutionSource });
      return;
    }

    this.trackedCacheHitTelemetry.add(telemetryKey);
    this.telemetryService.trackAddressResolutionResult(resolutionSource, durationMs, 'cache_hit', false, { source });
  }

  private writeToStorage(
    win: Window,
    key: string,
    entry: CachedGeocode,
    source: string,
    debugLog?: (msg: string, ...args: any[]) => void
  ): void {
    try {
      const lat = entry.result?.geometry?.location?.lat?.();
      const lng = entry.result?.geometry?.location?.lng?.();
      if (typeof lat !== 'number' || typeof lng !== 'number') {
        return;
      }

      const payload: SerializedCachedGeocode = {
        cachedAt: entry.cachedAt,
        result: {
          formatted_address: entry.result.formatted_address,
          address_components: entry.result.address_components,
          geometry: {
            location: {
              lat,
              lng,
            },
          },
        },
      };

      this.getStorage(win).setItem(this.storagePrefix + key, JSON.stringify(payload));
      debugLog?.('[MAPS-DEBUG] [ADDR] STORAGE_SAVE', { source, key, storageMode: this.storageMode });
      this.metrics.storageSaves += 1;
    } catch (err) {
      debugLog?.('[MAPS-DEBUG] [ADDR] STORAGE_SAVE', { source, key, skipped: true, err, storageMode: this.storageMode });
    }
  }

  private logMetricsSummary(
    debugLog: ((msg: string, ...args: any[]) => void) | undefined,
    source: string,
    key: string
  ): void {
    debugLog?.('[MAPS-DEBUG] [ADDR] METRICS', {
      source,
      key,
      ...this.metrics,
    });
  }

  private getStorage(win: Window): Storage {
    return this.storageMode === 'session' ? win.sessionStorage : win.localStorage;
  }

  private normalizeAddress(rawAddress: string): string {
    const replacements: Array<[RegExp, string]> = [
      [/\bstreet\b/g, 'st'],
      [/\bavenue\b/g, 'ave'],
      [/\broad\b/g, 'rd'],
      [/\bdrive\b/g, 'dr'],
      [/\blane\b/g, 'ln'],
      [/\bboulevard\b/g, 'blvd'],
      [/\bplace\b/g, 'pl'],
      [/\bcourt\b/g, 'ct'],
      [/\bterrace\b/g, 'ter'],
      [/\bparkway\b/g, 'pkwy'],
      [/\bcircle\b/g, 'cir'],
    ];

    let value = rawAddress
      .trim()
      .toLowerCase()
      .replace(/[.,#]/g, ' ')
      .replace(/\s+/g, ' ');

    for (const [pattern, replacement] of replacements) {
      value = value.replace(pattern, replacement);
    }

    return value.replace(/\s+/g, ' ').trim();
  }

  private normalizeZip(rawZip: string): string {
    return rawZip
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  }

  private cleanupInMemory(now: number): void {
    for (const [key, value] of this.cache.entries()) {
      const ageMs = now - value.cachedAt;
      if (ageMs >= this.ttlMs || ageMs >= this.maxRetentionMs) {
        this.cache.delete(key);
      }
    }
  }

  private cleanupStorage(
    win: Window,
    now: number,
    source: string,
    debugLog?: (msg: string, ...args: any[]) => void
  ): void {
    try {
      const storage = this.getStorage(win);
      const keys: string[] = [];
      const entries: Array<{ key: string; cachedAt: number }> = [];

      for (let i = 0; i < storage.length; i += 1) {
        const key = storage.key(i);
        if (!key || !key.startsWith(this.storagePrefix)) {
          continue;
        }
        keys.push(key);
        const raw = storage.getItem(key);
        if (!raw) {
          continue;
        }
        try {
          const parsed = JSON.parse(raw) as SerializedCachedGeocode;
          const cachedAt = parsed.cachedAt || 0;
          const ageMs = now - cachedAt;
          if (!cachedAt || ageMs >= this.maxRetentionMs) {
            storage.removeItem(key);
          } else {
            entries.push({ key, cachedAt });
          }
        } catch {
          storage.removeItem(key);
        }
      }

      if (entries.length > this.maxEntries) {
        entries
          .sort((a, b) => a.cachedAt - b.cachedAt)
          .slice(0, entries.length - this.maxEntries)
          .forEach((entry) => storage.removeItem(entry.key));
      }

      debugLog?.('[MAPS-DEBUG] [ADDR] STORAGE_CLEANUP', {
        source,
        storageMode: this.storageMode,
        maxEntries: this.maxEntries,
      });
    } catch (err) {
      debugLog?.('[MAPS-DEBUG] [ADDR] STORAGE_CLEANUP', { source, storageMode: this.storageMode, error: err });
    }
  }
}
